<template>
    <div v-if="internalErrorMessage" class="mb-3">
        <Message severity="error" :closable="false">
            <div v-html="internalErrorMessage"></div>
        </Message>
    </div>
    <div v-if="internalSuccessMessage" class="mb-3">
        <Message severity="success" :closable="false">
            <div v-html="internalSuccessMessage"></div>
        </Message>
    </div>

    <div v-show="showPaymentScreen">
        <subscription-checkout-header v-model="subscriptionDetails" />

        <div v-show="!isDisplaySecondConfirmation">
            <subscription-checkout
                ref="urlCheckout"
                v-model="subscriptionDetails"
                v-if="!internalErrorMessage"
                v-on:tier-quantity-changed="tierQuantityChangedAction"
            />

            <div class="text-center" v-if="!internalErrorMessage">
                <Button v-if="!isBusy" @click="stripeCheckoutAction">CONTINUE</Button>
                <div v-if="isBusy">
                    <Button :loading="true" outlined>PLEASE WAIT</Button>
                </div>
            </div>

            <div class="mt-2" v-if="!internalErrorMessage">
                <Message severity="secondary" class="p-3" :closable="false" v-html="getDescriptionHtml" />
            </div>
        </div>

        <div v-show="isDisplaySecondConfirmation">
            <Message severity="info" :closable="false">
                You have an active subscription until {{this.activeSubscriptionEndDate}}. Your new
                subscription will start when that one ends.
            </Message>

            <div class="text-center">
                <Button @click="stripeCheckoutContinueAction">YES, CONTINUE</Button>
            </div>
        </div>

        <stripe-checkout v-if="subscriptionDetails.stripeApiKey"
                         ref="checkoutRef"
                         :pk="subscriptionDetails.stripeApiKey"
                         :session-id="sessionId"
        />
    </div>
</template>
<script>
import { mapActions } from 'vuex'
import { subscriptionService } from "../../services";
import subscriptionCheckout from "./Checkout.vue";
import subscriptionCheckoutHeader from "./CheckoutHeader.vue";
import { StripeCheckout } from '@vue-stripe/vue-stripe';
import {setupEmbedExternalUrls} from "../../helpers/index.js";
import {getDiscountFormattedData, getPlanAmountForDiscount, getPriceFormattedData} from "../../helpers/global.js";

export default {
    components: {
        subscriptionCheckout, subscriptionCheckoutHeader, StripeCheckout
    },
    data () {
        return {
            isBusy: false,
            sessionId: null,
            activeSubscriptionEndDate: null,
            showPaymentScreen: true,
            internalErrorMessage: null,
            internalSuccessMessage: null,
            descriptionKey: 0,
        }
    },
    computed: {
        subscriptionDetails() {
            return window.subscriptionDetails;
        },
        isDisplaySecondConfirmation() {
            return this.activeSubscriptionEndDate !== null
        },
        getBasePlanData() {
            return window.subscriptionDetails.basePlan
        },
        getPlan() {
            const renewal = this.getBasePlanData
            return renewal && renewal.plan ? renewal.plan : null
        },
        getDiscount() {
            const renewal = this.getBasePlanData
            return renewal && renewal.discount ? renewal.discount : null
        },
        getRenewalData() {
            return window.subscriptionDetails.renewalPlan
        },
        getRenewalPlan() {
            const renewal = this.getRenewalData
            return renewal && renewal.plan ? renewal.plan : null
        },
        getRenewalDiscount() {
            const renewal = this.getRenewalData
            return renewal && renewal.discount ? renewal.discount : null
        },
        renewalDescriptionText() {
            this.descriptionKey; // use for recompute

            if (!this.getRenewalPlan) return null

            const {amount, currency, intervalText} = getPriceFormattedData(this.getRenewalPlan, this.subscriptionDetails)
            let discountText = ''
            let renewalAmount = amount

            let discount = this.getRenewalDiscount;
            if (discount) {
                const {value} = getDiscountFormattedData(discount)
                // update renewal amount for discount
                renewalAmount = getPlanAmountForDiscount(this.getRenewalPlan, discount)

                if (discount.duration === 'once') {
                    discountText = ` with a ${value} discount from the ${amount} ${currency} base price. Subsequent renewals are at full price`
                } else {
                    discountText = ` with a ${value} discount from the ${amount} ${currency} base price for the life of the subscription`
                }
            }

            return `
            <p><strong>Renewal</strong> ${intervalText} Subscription: ${renewalAmount} ${currency}${discountText}.</p>
            `
        },
        priceDescriptionText() {
            this.descriptionKey; // use for recompute

            if (!this.getPlan) return null

            const offerText = this.getRenewalPlan ? 'Intro offer' : 'Offer details'
            const {amount, currency, intervalText} = getPriceFormattedData(this.getPlan, this.subscriptionDetails)

            let offerDetails = '';
            if (this.subscriptionDetails.text && this.subscriptionDetails.text.beforePrice) {
                offerDetails = this.subscriptionDetails.text.beforePrice
            }

            return `
            <p>This subscription auto-renews, which can be cancelled at any time.</p>
            <p><strong>${offerText}</strong> ${intervalText} Subscription: ${amount} ${currency}. ${offerDetails}</p>
            `
        },
        getDescriptionHtml() {
            let html = ''

            if (this.getPlan) {
                html += this.priceDescriptionText
            }
            if (this.getRenewalPlan) {
                html += this.renewalDescriptionText
            }

            return html
        }
    },
    mounted () {
    },
    methods: {
        ...mapActions({
            'alertSuccess': 'alert/successPermanent',
            'alertError': 'alert/error',
        }),
        stripeCheckoutAction() {
            if (this.$refs.urlCheckout.v$.$invalid) {
                return
            }

            this.isBusy = true
            subscriptionService.createUrlSubscriptionSession(this.subscriptionDetails).then(
                response => {
                    this.isBusy = false

                    // check if this is prorate message
                    if (response.prorateMessage) {
                        this.showProrateMessage(response.prorateMessage)
                        return;
                    }

                    // if this is normal update flow
                    if (response.message) {
                        // show plan swap message and redirect to site
                        this.alertSuccess(response.message)
                        this.showPaymentScreen = false

                        let portalUrl = this.subscriptionDetails.portalUrl
                        if (portalUrl && response.enableRedirect) {
                            setTimeout(function() {
                                window.location.href = portalUrl
                            }, 5000);
                        }
                    } else {
                        // stop subscription or continue
                        if (response.errorMessage) {
                            this.internalErrorMessage = response.errorMessage
                        } else {
                            this.sessionId = response.sessionId
                            if (response.activeSubscriptionEndDate) {
                                this.activeSubscriptionEndDate = response.activeSubscriptionEndDate
                            } else {
                                this.stripeCheckoutContinueAction();
                            }
                        }
                    }
                },
                error => {
                    this.alertError(error)
                    this.isBusy = false
                }
            )
        },
        stripeCheckoutContinueAction() {
            this.$refs.checkoutRef.redirectToCheckout();
        },
        showProrateMessage(prorateMessage) {
            this.showPaymentScreen = false
            this.internalSuccessMessage = prorateMessage
            setTimeout(function() {
                setupEmbedExternalUrls()
            }, 500);
        },
        tierQuantityChangedAction(quantity) {
            this.subscriptionDetails.quantity = quantity
            // force re-render price details
            this.descriptionKey++;
        }
    },
}
</script>
